/**
 * The primitives every screen is built from.
 *
 * They render against the existing design system in src/theme/*.css rather
 * than carrying styles of their own, so the whole app stays one visual system
 * and a token change lands everywhere at once — MUI's `Box`/`Typography`
 * stand in for the plain `div`/`span`/`p` these used to be, but every
 * className is unchanged, so nothing here looks any different than before.
 * `Drawer` is the one real behaviour change: it now delegates to
 * `shared/components/CustomDialog`, so Escape-to-close and the focus trap are
 * MUI's built-in `Dialog` behaviour instead of a hand-rolled effect.
 */
import { useEffect, useState, type ReactNode } from 'react';
import {
	Box,
	Button,
	Divider,
	Drawer as MuiDrawer,
	IconButton,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ApiError, ApiOffline } from '../api/errors';
import CustomDialog from './components/CustomDialog';
import FormTextField from './components/form/text-field';
import { useForm } from 'react-hook-form';
import { InfoHint } from './InfoHint';
import { useTerm } from '../provider/auth';
import type { Vocabulary } from '../schemas';
import { humanise } from '../utils/format';

/** The vocabulary's tones, mapped onto the badge variants base.css defines. */
const TONE_TO_BADGE: Record<string, string> = {
	good: 'clean',
	warn: 'warning',
	bad: 'exception',
	busy: 'info',
	neutral: 'gray',
};

/**
 * A status, in the words the vocabulary endpoint gives — never the raw enum.
 * docs/ui-design.md §8: "Reading" not "parsing"; "Needs mapping" not AWAITING_MAPPING.
 */
export function StatusBadge({
	group,
	value,
	plain,
}: {
	group: keyof Vocabulary;
	value: string | null | undefined;
	plain?: boolean;
}) {
	const term = useTerm(group, value);
	if (!value) return <span className="mut">—</span>;

	// BLOCKING gets the solid treatment: it is the one status that stops a
	// report being issued, and it should not look like a sibling of WARNING.
	const variant = TONE_TO_BADGE[term.tone ?? 'neutral'] ?? 'gray';

	return (
		<Box
			component="span"
			className={`bdg ${variant}${plain ? ' plain' : ''}`}
			title={term.help || undefined}
		>
			{term.label}
		</Box>
	);
}

export function Card({
	title,
	hint,
	actions,
	children,
	footer,
	className = '',
}: {
	title?: ReactNode;
	hint?: ReactNode;
	actions?: ReactNode;
	children: ReactNode;
	footer?: ReactNode;
	className?: string;
}) {
	return (
		<Box component="section" className={`card ${className}`}>
			{(title || actions) && (
				<Box component="header" className="card-hd">
					{/* The hint is an (i) beside the title rather than a paragraph under
              it. One edit here de-clutters every card in the app — the text is
              still there, just not competing with the data for the reader's
              first glance. */}
					<div className="hd-title">
						{title && <h2>{title}</h2>}
						{hint && <InfoHint>{hint}</InfoHint>}
					</div>
					{actions && <div className="row">{actions}</div>}
				</Box>
			)}
			<Box className="card-bd">{children}</Box>
			{footer && (
				<Box component="footer" className="card-ft">
					{footer}
				</Box>
			)}
		</Box>
	);
}

/**
 * One figure. `meta` carries the threshold or the denominator, because docs/04
 * §8 requires that anything flagged says what limit it crossed.
 */
export type MetricTone = 'bad' | 'warn' | 'ok' | 'accent' | 'info';

/** Per tone: the left edge, the icon square, the background wash, the number. */
const METRIC_TONES: Record<
	MetricTone | 'neutral',
	{
		edge: string;
		iconBg: string;
		iconInk: string;
		wash: string;
		value: string;
	}
> = {
	neutral: {
		edge: 'var(--line-strong)',
		iconBg: 'var(--surface-sunken)',
		iconInk: 'var(--ink-2)',
		wash: 'var(--surface)',
		value: 'var(--ink)',
	},
	accent: {
		edge: 'var(--accent-solid)',
		iconBg: 'var(--accent-surface)',
		iconInk: 'var(--accent)',
		wash: 'var(--surface)',
		value: 'var(--ink)',
	},
	info: {
		edge: 'var(--info-solid)',
		iconBg: 'var(--info-surface)',
		iconInk: 'var(--info-ink)',
		wash: 'var(--surface)',
		value: 'var(--ink)',
	},
	ok: {
		edge: 'var(--ok-solid)',
		iconBg: 'var(--ok-surface)',
		iconInk: 'var(--ok-ink)',
		wash: 'var(--surface)',
		value: 'var(--ink)',
	},
	warn: {
		edge: 'var(--warn-solid)',
		iconBg: 'var(--warn-surface)',
		iconInk: 'var(--warn-ink)',
		wash: 'var(--surface)',
		value: 'var(--ink)',
	},
	bad: {
		edge: 'var(--danger-solid)',
		iconBg: 'var(--danger-surface)',
		iconInk: 'var(--danger-solid)',
		wash: 'var(--surface)',
		value: 'var(--danger-ink)',
	},
};

/** One number card: an optional icon in a tinted square, the label, the
 *  number and a note under it. `tone` colours the edge, the icon and the
 *  number (bad = red, for findings and blockers). */
export function MetricCard({
	label,
	value,
	unit,
	meta,
	tone,
	icon,
	small,
}: {
	label: ReactNode;
	value: ReactNode;
	unit?: string;
	meta?: ReactNode;
	tone?: MetricTone;
	icon?: ReactNode;
	small?: boolean;
}) {
	const look = METRIC_TONES[tone ?? 'neutral'];
	return (
		<Box
			sx={{
				position: 'relative',
				display: 'flex',
				alignItems: 'flex-start',
				gap: 'var(--s3)',
				minWidth: 0,
				height: '100%',
				boxSizing: 'border-box',
				p: '14px 16px 14px 19px',
				background: `linear-gradient(120deg, var(--surface) 55%, ${look.wash})`,
				border: '1px solid var(--line)',
				borderRadius: '10px',
				boxShadow: 'var(--e1)',
				overflow: 'hidden',
				transition:
					'border-color var(--dur-fast) var(--ease), box-shadow var(--dur-fast) var(--ease), transform var(--dur-fast) var(--ease)',
				/* The coloured left edge. */
				'&::before': {
					content: '""',
					position: 'absolute',
					inset: '0 auto 0 0',
					width: '3px',
					background: look.edge,
				},
			}}
		>
			{icon && (
				<Box
					aria-hidden="true"
					sx={{
						flex: '0 0 36px',
						height: 36,
						borderRadius: '10px',
						display: 'grid',
						placeItems: 'center',
						bgcolor: look.iconBg,
						color: look.iconInk,
						'& svg': { fontSize: 20 },
					}}
				>
					{icon}
				</Box>
			)}
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '2px',
					minWidth: 0,
				}}
			>
				<Typography
					component="span"
					sx={{
						fontSize: 'var(--fs-small)',
						fontWeight: 600,
						color: 'var(--ink-2)',
					}}
				>
					{label}
				</Typography>
				<Typography
					component="span"
					sx={{
						fontSize: small
							? 'var(--fs-section)'
							: 'var(--fs-figure)',
						fontWeight: 600,
						lineHeight: 1.2,
						color: look.value,
						fontVariantNumeric: 'tabular-nums',
						letterSpacing: 'var(--tracking-tight)',
					}}
				>
					{value}
					{unit && (
						<Typography
							component="span"
							sx={{
								ml: '3px',
								fontSize: 'var(--fs-small)',
								color: 'var(--ink-3)',
							}}
						>
							{unit}
						</Typography>
					)}
				</Typography>
				{meta && (
					<Typography
						component="span"
						sx={{
							fontSize: 'var(--fs-small)',
							color: 'var(--ink-3)',
							lineHeight: 'var(--lh-snug)',
						}}
					>
						{meta}
					</Typography>
				)}
			</Box>
		</Box>
	);
}

export function Stat({
	tone,
	...card
}: Omit<Parameters<typeof MetricCard>[0], 'tone'> & {
	tone?: 'danger' | 'warn' | 'ok' | 'accent';
}) {
	return <MetricCard {...card} tone={tone === 'danger' ? 'bad' : tone} />;
}

export function Spinner({ label }: { label?: string }) {
	return (
		<Box className="center stack" role="status" aria-live="polite">
			<span className="spinner" />
			{label && <p className="mut small">{label}</p>}
		</Box>
	);
}

export function Empty({
	title,
	children,
	action,
	icon,
}: {
	title: string;
	children?: ReactNode;
	action?: ReactNode;
	/** A muted glyph above the title. */
	icon?: ReactNode;
}) {
	return (
		<Box className="empty">
			{icon && (
				<Box
					aria-hidden="true"
					sx={{
						color: 'var(--ink-3)',
						mb: 'var(--s2)',
						'& svg': { fontSize: 32 },
					}}
				>
					{icon}
				</Box>
			)}
			<Typography className="b">{title}</Typography>
			{children && <p className="mut small">{children}</p>}
			{action && <div className="mt3">{action}</div>}
		</Box>
	);
}

/**
 * An error, with the technical detail one click away rather than in the
 * auditor's face (docs/ui-design.md §8).
 *
 * `error: unknown`, deliberately.
 *
 * What a screen catches is whatever was thrown — an `ApiError`, an `ApiOffline`
 * when the API is not running, or something unforeseen. Typing this as
 * `ApiError` would push a cast to every call site, and the cast would be a lie
 * the first time a fetch failed outright. It is narrowed here, once.
 */
export function ErrorNotice({
	error,
	onRetry,
}: {
	error: unknown;
	onRetry?: () => void;
}) {
	/* Bring the failure into view.

     Every form on this app puts its error at the TOP and its submit button at
     the BOTTOM. On the New audit page that is a screen apart, so a rejected
     submit looked exactly like a click that did nothing — the reason was on
     the page the whole time, above the fold.

     Keyed on the error object via the ref callback below, which fires again
     any time the DOM node it's attached to changes identity — which for this
     component means every render while `error` is truthy, same as the
     previous effect-based version keyed its dependency on `error`. */
	if (!error) return null;

	const offline = error instanceof ApiOffline;
	/* What happened, in words a person can act on. A server that did not answer
     is not the person's fault and loses nothing. */
	const message = offline
		? 'Reconcilio cannot reach the server right now.'
		: error instanceof Error
			? error.message
			: 'Something went wrong.';
	const fields =
		error instanceof ApiError
			? error.fields
			: ({} as Record<string, string>);
	const detail = error instanceof ApiError ? error.detail : '';

	return (
		<Box
			className="note danger"
			role="alert"
			ref={(node: HTMLDivElement | null) =>
				node?.scrollIntoView({ behavior: 'smooth', block: 'center' })
			}
		>
			<Typography className="b">{message}</Typography>
			{offline && (
				<p className="small">
					Your work is saved. Try again in a moment.
				</p>
			)}
			{Object.entries(fields).length > 0 && (
				<ul className="small">
					{Object.entries(fields).map(([field, fieldMessage]) => (
						<li key={field}>{fieldMessage}</li>
					))}
				</ul>
			)}
			{detail && (
				<details className="small mut">
					<summary>Show details</summary>
					{detail}
				</details>
			)}
			{onRetry && (
				<div className="row mt3">
					<Button variant="outlined" size="small" onClick={onRetry}>
						Try again
					</Button>
				</div>
			)}
		</Box>
	);
}

/** A plain-language note. Used for "why this is blocked" explanations. */
export function Note({
	tone = 'info',
	title,
	row = false,
	dismissible = false,
	autoHide,
	children,
}: {
	tone?: 'info' | 'warn' | 'danger' | 'ok' | 'gray';
	title?: ReactNode;
	/**
	 * Title and text on one line.
	 *
	 * For a note that states a CONDITION — no worker, one step failed — rather
	 * than explaining something. Three lines of reassurance above a table is
	 * three lines of the table nobody can see, and the reader who needs the
	 * detail is going to the step that carries it anyway.
	 */
	row?: boolean;
	/**
	 * Adds a close control so a person can clear this once read.
	 *
	 * The CALLER still decides whether the note renders at all — this only
	 * lets a reader dismiss one early rather than staring at it for the rest
	 * of the session. If a call site's condition can change to a genuinely
	 * NEW one (a different blocker, a different failure) after an old one was
	 * dismissed, give it `key={theCondition}` so React remounts it — this
	 * component has no way to tell "new problem" from "same problem, parent
	 * re-rendered" on its own, and guessing wrong in either direction is
	 * worse than leaving that call.
	 */
	dismissible?: boolean;
	/**
	 * Milliseconds until the note hides itself — for a transient
	 * acknowledgement ("saved", "run started") that reads better sitting next
	 * to the thing it's about than in the global toast queue.
	 *
	 * Never pass this for a condition that can still be true after the
	 * timer — a blocking checklist item that vanishes on a clock is not
	 * cleared, it's hidden, and the two must never look the same (house
	 * rule). Use `dismissible` for those instead, or nothing at all.
	 */
	autoHide?: number;
	children: ReactNode;
}) {
	const [hidden, setHidden] = useState(false);

	useEffect(() => {
		if (!autoHide) return;
		// Starts once, on mount — "show it for a few seconds on first load",
		// not a timer that restarts on every unrelated re-render.
		const timer = setTimeout(() => setHidden(true), autoHide);
		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (hidden) return null;

	const close = dismissible && (
		<IconButton
			size="small"
			aria-label="Dismiss"
			onClick={() => setHidden(true)}
			sx={{ marginLeft: 'auto', flex: '0 0 auto' }}
		>
			<CloseIcon fontSize="small" />
		</IconButton>
	);

	// base.css has no `.note.info`: the bare `.note` IS the informational style.
	//
	// Three branches, not one, so the ~60 existing call sites that pass
	// neither `dismissible` nor `row` render EXACTLY the markup they always
	// have — the dismiss button's wrapper row would otherwise strip the
	// title paragraph's default margin for everyone, not just itself.
	if (row) {
		return (
			<Box className={`note${tone === 'info' ? '' : ` ${tone}`} one`}>
				{/* One paragraph, not title-then-text as two flex items — a long
            title used to force itself onto its own line with the sentence
            wrapping below it, which is the tall, boxy look this exists to
            avoid. Bold the title inline and let the whole thing wrap as one
            run of text, same as a normal sentence would. */}
				<Typography
					className="small"
					component="p"
					style={{ margin: 0 }}
				>
					{title && (
						<Box component="span" className="b">
							{title}:{' '}
						</Box>
					)}
					{children}
				</Typography>
				{close}
			</Box>
		);
	}
	if (dismissible) {
		return (
			<Box className={`note${tone === 'info' ? '' : ` ${tone}`}`}>
				<div className="row" style={{ alignItems: 'flex-start' }}>
					{title ? (
						<Typography
							className="b"
							style={{ margin: 0, flex: '1 1 auto' }}
						>
							{title}
						</Typography>
					) : (
						<span style={{ flex: '1 1 auto' }} />
					)}
					{close}
				</div>
				<div className="small">{children}</div>
			</Box>
		);
	}
	return (
		<Box className={`note${tone === 'info' ? '' : ` ${tone}`}`}>
			{title && <Typography className="b">{title}</Typography>}
			<div className="small">{children}</div>
		</Box>
	);
}

export function Progress({
	pct,
	note,
	/** One line rather than a centred block. For a table cell, where the
	 *  run-card proportions leave a percentage stranded above a wide empty
	 *  track. */
	compact = false,
}: {
	pct: number;
	note?: string;
	compact?: boolean;
}) {
	const width = `${Math.min(100, Math.max(0, pct))}%`;
	return (
		<Box className={`prog-shell${compact ? ' compact' : ''}`}>
			<div className="prog-hd">
				<span className="small b">{Math.round(pct)}%</span>
				{note && <span className="prog-msg mut small">{note}</span>}
			</div>
			<div
				className="prog-track"
				role="progressbar"
				aria-valuenow={Math.round(pct)}
				aria-valuemin={0}
				aria-valuemax={100}
				aria-valuetext={
					note ? `${Math.round(pct)}% — ${note}` : undefined
				}
			>
				{/* An <i>, not a <div>. `.prog-track > i` is what carries the accent
            fill; a div matched `.bar` instead — whose background is the pale
            TRACK tint — so the bar rendered as an empty grey rail at every
            percentage and looked like nothing was happening. */}
				<i style={{ width }} />
			</div>
		</Box>
	);
}

/**
 * Despite the name — and despite the docs/ui-design.md §6 "queue stays on screen behind
 * it" reasoning this was originally built for — `.modal.drawer` was changed
 * at some point to render CENTERED (`place-items: center`, panel width
 * `min(720px, 100%)`), same as every hand-rolled modal in the app. Confirmed
 * directly against the old `.modal.drawer` rule, not assumed
 * from the component's name. So this uses `CustomDialog`'s default `'center'`
 * placement, not `'right'` — the same `.modal-panel` className still governs
 * width/height/appearance exactly as before; Escape-to-close and the focus
 * trap are now MUI `Dialog`'s own behaviour instead of the hand-rolled effect
 * this used to carry.
 */
export function Dialog({
	open,
	onClose,
	title,
	subtitle,
	footer,
	children,
	width,
	plainFooter = false,
}: {
	open: boolean;
	onClose: () => void;
	title: ReactNode;
	subtitle?: ReactNode;
	footer?: ReactNode;
	children: ReactNode;
	/** Overrides `.modal-panel`'s fixed 720px — for a dialog that is a
	 *  sentence and two buttons, not a form or a table. */
	width?: number | string;
	/** Drops `.card-ft`'s sunken grey fill, keeping just the separator line —
	 *  that fill reads as a heavy band under two buttons on a small dialog,
	 *  even though it's right for a card's own footer or a form-sized one. */
	plainFooter?: boolean;
}) {
	return (
		<CustomDialog
			Open={open}
			handleClose={onClose}
			backdropClick
			width={width}
			otherProps={{
				PaperProps: { className: 'modal-panel' },
			}}
			dialogTitleJsx={
				<Box component="header" className="card-hd">
					<div>
						<h2>{title}</h2>
						{subtitle && <p className="hint">{subtitle}</p>}
					</div>
					<IconButton
						onClick={onClose}
						aria-label="Close"
						size="small"
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>
			}
			dialogActions={
				footer ? (
					<Box sx={{ width: '100%' }}>
						{/* Same reasoning as the header's `Divider` above — an explicit
                sibling instead of `.card-ft`'s own `border-top`, which had
                the identical not-quite-full-width problem. */}
						<Divider sx={{ margin: 0 }} />
						<Box
							component="footer"
							className="card-ft"
							sx={{
								borderTop: 'none !important',
								...(plainFooter && {
									/* `!important`: `StyledEngineProvider injectFirst` (see
                     `main.tsx`) puts MUI's own styles first in the
                     stylesheet, so legacy `.card-ft` — loaded after, via
                     `index.html`'s `<link>` — wins an equal-specificity tie
                     against a plain `sx` override. Same fix as the
                     `OutlinedInput` double-border bug, same reason. */
									background: 'none !important',
									borderRadius: '0 !important',
								}),
							}}
						>
							{footer}
						</Box>
					</Box>
				) : undefined
			}
		>
			<Box className="card-bd scrolly">{children}</Box>
		</CustomDialog>
	);
}

/**
 * Row detail that keeps the list visible: a panel from the right.
 *
 * Escape closes it and focus returns to the row that opened it (MUI Drawer's
 * own modal behaviour). Use `Dialog` for a decision; this for reading.
 */
export function SideDrawer({
	open,
	onClose,
	title,
	subtitle,
	footer,
	children,
	width = 560,
}: {
	open: boolean;
	onClose: () => void;
	title: ReactNode;
	subtitle?: ReactNode;
	footer?: ReactNode;
	children: ReactNode;
	width?: number;
}) {
	return (
		<MuiDrawer
			anchor="right"
			open={open}
			onClose={onClose}
			PaperProps={{
				sx: {
					width: `min(${width}px, 100vw)`,
					display: 'flex',
					flexDirection: 'column',
				},
			}}
		>
			<Box
				component="header"
				className="card-hd"
				sx={{ alignItems: 'flex-start' }}
			>
				<div style={{ minWidth: 0 }}>
					<h2>{title}</h2>
					{subtitle && <p className="hint">{subtitle}</p>}
				</div>
				<IconButton onClick={onClose} aria-label="Close" size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</Box>
			<Divider />
			<Box
				className="card-bd"
				sx={{ flex: '1 1 auto', overflowY: 'auto' }}
			>
				{children}
			</Box>
			{footer && (
				<>
					<Divider />
					<Box
						component="footer"
						className="card-ft"
						sx={{ background: 'none !important' }}
					>
						{footer}
					</Box>
				</>
			)}
		</MuiDrawer>
	);
}

/* The form controls live in ./fields — they are a body of work in their own
   right (a portalled listbox and a month grid) rather than a wrapper over a
   CSS class like everything else here. They are re-exported so the existing
   `from '../shared/ui'` imports keep resolving and there is still one place
   a screen imports its primitives from. */
export {
	Field,
	Select,
	MultiSelect,
	DatePicker,
	formatDisplayDate,
} from './fields';
export type { Option } from './fields';

/**
 * The one confirmation dialog.
 *
 * States the action as the title, what changes in `question`, the consequence
 * in `impact`, and — for a recorded decision (exception decisions, excluding a
 * file, overriding a failed checklist) — requires a written reason that goes on
 * the record. The confirm button repeats the action; `danger` styles it red for
 * an irreversible action. Escape closes it and focus returns to the trigger.
 */
export function ConfirmDialog({
	open,
	onClose,
	onConfirm,
	title,
	question,
	impact,
	confirmLabel,
	reasonRequired = false,
	reasonLabel = 'Reason',
	reasonHint,
	pending,
	error,
	danger = false,
}: {
	open: boolean;
	onClose: () => void;
	onConfirm: (reason: string) => void;
	title: string;
	/** What will happen, in a sentence or two. */
	question?: ReactNode;
	/** What it changes for the record — shown as a highlighted note. */
	impact?: ReactNode;
	confirmLabel: string;
	reasonRequired?: boolean;
	reasonLabel?: string;
	reasonHint?: ReactNode;
	pending?: boolean;
	error?: unknown;
	danger?: boolean;
}) {
	const { control, watch, reset } = useForm<{ reason: string }>({
		defaultValues: { reason: '' },
	});
	const reason = watch('reason');

	useEffect(() => {
		if (open) reset({ reason: '' });
	}, [open, reset]);

	const canConfirm =
		!pending && (!reasonRequired || reason.trim().length > 0);
	const fieldError =
		error instanceof ApiError ? error.fields.reason : undefined;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			title={title}
			width={reasonRequired ? 560 : 480}
			plainFooter
			footer={
				<Box
					sx={{
						display: 'flex',
						gap: 'var(--s2)',
						justifyContent: 'flex-end',
					}}
				>
					<Button
						variant="outlined"
						onClick={onClose}
						disabled={pending}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						color={danger ? 'error' : 'primary'}
						loading={pending}
						disabled={!canConfirm}
						onClick={() => onConfirm(reason.trim())}
					>
						{confirmLabel}
					</Button>
				</Box>
			}
		>
			<div className="stack">
				{question && <div>{question}</div>}
				{impact && (
					<Note tone="warn" title="What this changes">
						{impact}
					</Note>
				)}
				<ErrorNotice error={error ?? null} />
				{reasonRequired && (
					<FormTextField
						control={control}
						name="reason"
						label={reasonLabel}
						isRequired
						muiProps={{
							multiline: true,
							rows: 4,
							placeholder: 'Why is this the right decision?',
							helperText:
								fieldError ??
								reasonHint ??
								'This goes on the permanent record. Write what a reviewer would need to know.',
							error: Boolean(fieldError),
						}}
					/>
				)}
			</div>
		</Dialog>
	);
}

/** A key/value row, for detail panels. */
export function Detail({
	label,
	children,
}: {
	label: ReactNode;
	children: ReactNode;
}) {
	return (
		<Box
			className="row spread"
			style={{ alignItems: 'baseline', gap: 'var(--s4)' }}
		>
			<span className="small mut nowrap">{label}</span>
			<span className="small" style={{ textAlign: 'right', minWidth: 0 }}>
				{children}
			</span>
		</Box>
	);
}

export function createPageHeaderSubTitleForClients(
	code?: string | null,
	gstin?: string | null,
) {
	return (
		['Code: ' + code, gstin ? 'GST: ' + gstin : '']
			.filter(Boolean)
			.join(' | ') || undefined
	);
}

export { humanise };
